import { Link } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { BLOG_SEO, GUIDES, GUIDE_TOOLS, guidePath } from '../data/guides';
import { GuideCta, GuideIcon } from '../components/guide/GuideParts';

// Route: /blog — the AC Guides hub (Oct 2026). White cards in the site's look,
// blue kept for labels and links: the live guides two up, the AC calculator,
// then the guides coming next (no link until they exist).

export default function Blog() {
  const live = GUIDES.filter((g) => g.live);
  const next = GUIDES.filter((g) => !g.live);
  return (
    <>
      <Helmet>
        <title>{BLOG_SEO.title}</title>
        <meta name="description" content={BLOG_SEO.meta} />
        <meta property="og:title" content={BLOG_SEO.title} />
        <meta property="og:description" content={BLOG_SEO.meta} />
      </Helmet>
      <main className="bg-white pb-16 pt-16 lg:pt-20">
        <div className="mx-auto max-w-[760px] px-4">
          <nav aria-label="Breadcrumb" className="flex gap-1.5 pt-3.5 text-[13px] text-gray-500">
            <Link to="/" className="hover:text-blue-600">Home</Link><span aria-hidden="true">›</span><span className="text-gray-600">AC Guides</span>
          </nav>
          <p className="mt-3.5 text-[12.5px] font-bold uppercase tracking-wider text-blue-600">Customer Corner</p>
          <h1 className="mt-2 text-[30px] font-extrabold tracking-tight text-gray-900 sm:text-[40px]">{BLOG_SEO.h1}</h1>
          <p className="mt-2 max-w-[62ch] text-gray-600">{BLOG_SEO.intro}</p>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {live.map((g) => (
              <Link key={g.slug} to={guidePath(g)}
                className="group grid grid-cols-[44px_minmax(0,1fr)] content-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 transition hover:border-blue-300 hover:shadow-[0_8px_24px_rgba(17,24,39,0.07)] sm:grid-cols-1">
                <GuideIcon name={g.icon} />
                <span className="grid gap-1">
                  <span className="text-[11.5px] font-bold uppercase tracking-wide text-gray-500">
                    <span className="text-blue-700">Guide</span>
                    {g.minutes ? ` · ${g.minutes} min` : ''}
                    {g.tools ? (g.tools > 1 ? ' · animation and tool' : ' · animation') : ''}
                  </span>
                  <span className="text-[17px] font-extrabold leading-snug text-gray-900 sm:text-[18px]">{g.title}</span>
                  <span className="text-sm text-gray-600">{g.blurb}</span>
                  <span className="mt-1 text-sm font-bold text-blue-700 group-hover:text-blue-800">Read the guide ›</span>
                </span>
              </Link>
            ))}
            {GUIDE_TOOLS.map((t) => (
              <Link key={t.href} to={t.href}
                className="group grid grid-cols-[44px_minmax(0,1fr)] content-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 transition hover:border-green-300 hover:shadow-[0_8px_24px_rgba(17,24,39,0.07)] sm:grid-cols-1">
                <GuideIcon name={t.icon || 'calculator'} tone="green" />
                <span className="grid gap-1">
                  <span className="text-[11.5px] font-bold uppercase tracking-wide text-green-700">Live tool</span>
                  <span className="text-[17px] font-extrabold leading-snug text-gray-900 sm:text-[18px]">{t.title}</span>
                  <span className="text-sm text-gray-600">{t.blurb}</span>
                  <span className="mt-1 text-sm font-bold text-green-700 group-hover:text-green-800">Open the tool ›</span>
                </span>
              </Link>
            ))}
          </div>

          {next.length > 0 && (
            <>
              <h2 className="mt-9 text-xl font-extrabold text-gray-900">Coming next</h2>
              <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                {next.map((g) => (
                  <div key={g.title} className="grid gap-1 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3.5 py-3">
                    <span className="text-[15px] font-bold leading-snug text-gray-900">{g.title}</span>
                    <span className="text-[13px] text-gray-600">{g.blurb}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <GuideCta source="guides_hub" title="Have a question we haven't answered yet?" />
        </div>
      </main>
    </>
  );
}
